<template lang="pug">
FcwSheet.FcwStationSheet(
  v-model="isOpen"
  position="left"
  :title="name"
  :loading="loading"
  type="floating"
  @open="emit('open')"
  @close="emit('close')"
)
  template(#subtitle)
    .FcwStationSheet__tags
      FcwChip(
        v-for="tag in tags"
        :key="tag"
        :label="tag"
        color="primary"
      )
  template(#content)
    .FcwStationSheet__content
      .FcwStationSheet__availabilities(v-if="!isClosed")
        FSkeleton(
          v-if="loading"
          width="40%"
          height="72px"
        )
        FcwEntityAvailabilityItem(
          v-else
          :count="availableBikes ?? 0"
          icon="zoovBikeAlt"
          :label="t('entities__bikes__available', {}, availableBikes)"
        )
        FDivider(
          vertical
          height="72px"
          width="1px"
          color="neutral--light-2"
        )
        FSkeleton(
          v-if="loading"
          width="40%"
          height="72px"
        )
        FcwEntityAvailabilityItem(
          v-else
          :count="availableSlots ?? 0"
          icon="parking"
          :label="t('entities__parkings__available', {}, availableSlots)"
        )

      FSkeleton(
        v-if="loading"
        width="100%"
        height="64px"
      )
      FcwAddress(
        v-else-if="address?.lines?.length"
        :address="address?.lines ?? []"
        :coordinates="address?.coordinates"
        :show-route="Boolean(address?.coordinates)"
        :walking-time="walkingTime"
        :distance="distance"
      )

      FcwBanner(
        v-if="isClosed"
        color="danger"
        icon="circleSlash"
        :text="closedStationText"
      )

    span.FcwStationSheet__lastUpdated(v-if="lastUpdatedAt") {{ t('entities__last_updated_at') }} {{ formatDate(lastUpdatedAt, { format: 'p' }) }}
</template>

<style lang="stylus">
.FcwStationSheet__tags
  display flex
  gap rem(8)
  text-transform uppercase

.FcwStationSheet__content
  display flex
  flex-direction column
  gap rem(16)
  height inherit

.FcwStationSheet__availabilities
  display flex
  justify-content space-evenly
  align-items center
  gap rem(16)

  .FDivider
    flex-shrink 0

.FcwStationSheet__lastUpdated
  margin-top auto
</style>

<script setup lang="ts">
export interface FcwStationSheetProps {
  /**
   * Model value of the sheet
   */
  modelValue: boolean;
  /**
   * Name of the station
   */
  name: string;
  /**
   * Tags of the station
   */
  tags?: string[];
  /**
   * Address of the station
   */
  address?: {
    lines: string[];
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  /**
   * Walking time to the station
   */
  walkingTime?: number | null;
  /**
   * Distance to the station
   */
  distance?: number | null;
  /**
   * Available bikes in the station
   */
  availableBikes?: number;
  /**
   * Available slots in the station
   */
  availableSlots?: number;
  /**
   * Whether the station is open
   */
  isClosed?: boolean;
  /**
   * Date until the station is closed
   */
  closedUntil?: Date | null;
  /**
   * Loading state of the sheet
   */
  loading?: boolean;
  /**
   * Last time the data has been refreshed
   */
  lastUpdatedAt?: Date | null;
}

const props = withDefaults(defineProps<FcwStationSheetProps>(), {
  tags: () => [],
  address: undefined,
  walkingTime: undefined,
  distance: undefined,
  availableBikes: undefined,
  availableSlots: undefined,
  isClosed: false,
  closedUntil: null,
  loading: false,
  lastUpdatedAt: null,
});

const isOpen = useVModelProxy({ props });

const closedStationText = computed(() => {
  const showClosedUntilText =
    props.isClosed && props.closedUntil && props.closedUntil > new Date();

  return showClosedUntilText
    ? t('entities__station__closed_until', {
        date: formatDate(props.closedUntil, { format: 'PPPP' }),
      })
    : t('entities__station__closed');
});

const emit = defineEmits<{
  (name: 'open'): void;
  (name: 'close'): void;
}>();

const { formatDate } = useLocaleFormat();

const { t } = useClientI18n();
</script>
